import React, { useState, useEffect, useCallback } from 'react';

import { hangupCall } from 'flow/actions/top-actions';
import {
    answerCompanionCall,
    holdCompanionCall,
    unholdCompanionCall,
} from 'flow/companion/companion-actions';
import { isCompanionSignallingSurface } from 'flow/companion/companion-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { TypedMap } from 'flow/types/TypedMap';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import CallItem from 'flow/components/communication-canvas/CallItem';
import {
    ActiveCallDirections,
    CallState,
    convertNumberToE164,
} from 'flow/utils/call-utils';
import { useAppSelector } from 'flow/hooks/useRedux';

interface Props {
    activeCall: TypedMap<ActiveLineData>;
    spawnedNotifications: string[];
    onNotificationSpawned: (notificationDedupKey: string) => void;
}

const RemoteCall = ({
    activeCall,
    spawnedNotifications,
    onNotificationSpawned,
}: Props) => {
    const ownCountryCode = useAppSelector(getOwnCountryCode);
    const callKey = activeCall.get('key');
    const callId = activeCall.get('callId');

    useEffect(() => {
        addUserPhoneLogEntry(
            `RemoteCall component mounted, callKey: ${callKey}`,
        );
        return () => {
            addUserPhoneLogEntry(
                `RemoteCall component unmounted, callKey: ${callKey}`,
            );
        };
    }, [callKey]);

    const [isEnded, setIsEnded] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const callState = CallState.get(activeCall.get('state'));
    const [isAccepted, setIsAccepted] = useState(CallState.UP.is(callState));

    useEffect(() => {
        if (!isStarted && !CallState.DOWN.is(callState)) {
            setIsStarted(true);
        }
    }, [isStarted, callState]);

    useEffect(() => {
        if (
            (isStarted && CallState.DOWN.is(callState)) ||
            CallState.BUSY.is(callState)
        ) {
            setIsEnded(true);
        }
    }, [isStarted, callState]);

    useEffect(() => {
        setIsAccepted(CallState.UP.is(callState));
    }, [callState]);

    const onHangup = useCallback(() => {
        setIsEnded(true);
        hangupCall(callKey);
        addUserPhoneLogEntry(
            `Remote call -> Hangup clicked for call with callKey ${callKey}`,
        );
    }, [callKey]);

    // Model A: when this remote call lives on the user's companion, answering it is delegated to
    // the companion over the backend (Home -> TOP push).
    //
    // Gated on being a signalling surface with the feature enabled - not merely on the feature
    // switch, which is per user and therefore also true in that user's ordinary web app - and on
    // the user leaving "Use Companion for calls" on.
    //
    // Note what that still does not buy us: the designation ("Use as Companion") settles which
    // install acts on commands, but nothing here proves this particular remote call is on that
    // machine rather than the user's mobile or desk phone. Pressing answer on a call that is
    // ringing elsewhere sends the command to the companion regardless.
    const isCompanionMode = useAppSelector(isCompanionSignallingSurface);
    const { i18next } = useAppContext();

    // Each of these three relays a command to another machine, so they guard on companion mode
    // themselves rather than trusting the call site. Whether the *button* appears is a separate
    // question and stays in the JSX below, because CallItem renders a control only when it is
    // handed a handler - moving isAccepted/isOnHold in here would show a dead hold button on a
    // ringing call.
    const onAnswer = useCallback(() => {
        if (!isCompanionMode) {
            return;
        }
        addUserPhoneLogEntry(
            `Remote call -> Answer clicked for call with callId ${callId}`,
        );
        answerCompanionCall(callId);
    }, [callId, isCompanionMode]);

    // In companion mode the CTI is a full control surface, not a read-only indicator, so hold and
    // unhold are relayed the same way answer is. Hold state is tracked optimistically - nothing
    // reports the companion's real state back yet.
    const [isOnHold, setIsOnHold] = useState(false);
    const onHold = useCallback(() => {
        if (!isCompanionMode) {
            return;
        }
        addUserPhoneLogEntry(
            `Remote call -> Hold clicked for call with callId ${callId}`,
        );
        holdCompanionCall(callId);
        setIsOnHold(true);
    }, [callId, isCompanionMode]);
    const onUnhold = useCallback(() => {
        if (!isCompanionMode) {
            return;
        }
        addUserPhoneLogEntry(
            `Remote call -> Unhold clicked for call with callId ${callId}`,
        );
        unholdCompanionCall(callId);
        setIsOnHold(false);
    }, [callId, isCompanionMode]);

    const direction =
        activeCall.get('direction') === ActiveCallDirections.OUTGOING.value
            ? 'outgoing'
            : 'incoming';
    const sourceKey = activeCall.get('originalCallerId')
        ? 'originalCallerId'
        : 'source';
    const remoteNumber =
        direction === 'outgoing'
            ? activeCall.get('destination')
            : activeCall.get(sourceKey);
    const localNumber =
        direction === 'outgoing'
            ? activeCall.get(sourceKey)
            : activeCall.get('destination');

    return (
        <CallItem
            callId={activeCall.get('callId')}
            direction={direction}
            remoteNumber={convertNumberToE164(remoteNumber, ownCountryCode)}
            localNumber={convertNumberToE164(localNumber, ownCountryCode)}
            ownCountryCode={ownCountryCode}
            startTime={new Date(activeCall.get('startTime'))}
            isAccepted={!!isAccepted}
            isEnded={isEnded}
            isOnHold={isOnHold}
            onHangup={onHangup}
            onAnswer={
                isCompanionMode && direction === 'incoming'
                    ? onAnswer
                    : undefined
            }
            onHold={
                isCompanionMode && isAccepted && !isOnHold ? onHold : undefined
            }
            onUnhold={
                isCompanionMode && isAccepted && isOnHold ? onUnhold : undefined
            }
            spawnedNotifications={spawnedNotifications}
            onNotificationSpawned={onNotificationSpawned}
            // The banner is the only sign the user gets that their audio is on another machine, so
            // companion mode keeps it and says which: silently moving someone's call to a different
            // computer and showing them an ordinary call canvas is how they end up talking into a
            // laptop that is not listening.
            isRemote={true}
            remoteLabel={
                isCompanionMode
                    ? i18next.t('call.canvas.call_through_desktop_app')
                    : undefined
            }
        />
    );
};

export default React.memo(RemoteCall);
